import { z } from "zod";
import { BusinessDate } from "./common.js";
import { Id } from "./ids.js";
import { jalaliMonthRange, type ProfitFoodRow } from "./profit.js";

export const MenuEngineeringGroup = z.enum(["star", "plowhorse", "puzzle", "dog"]);
export type MenuEngineeringGroup = z.infer<typeof MenuEngineeringGroup>;

export const MENU_ENGINEERING_GROUP_LABELS: Record<MenuEngineeringGroup, string> = {
  star: "ستاره",
  plowhorse: "اسب کاری",
  puzzle: "معما",
  dog: "سگ",
};

export const MenuEngineeringIncompleteReason = z.enum(["missing_cost", "missing_price", "missing_both"]);
export type MenuEngineeringIncompleteReason = z.infer<typeof MenuEngineeringIncompleteReason>;

export const MenuEngineeringFoodItem = z.object({
  menuItemId: z.string(),
  foodName: z.string(),
  portions: z.number().int().nonnegative(),
  popularitySharePerMille: z.number().int().nonnegative(),
  marginPerPortionRial: z.number().int(),
  totalRevenueRial: z.number().int(),
  totalCostRial: z.number().int(),
  totalMarginRial: z.number().int(),
  group: MenuEngineeringGroup,
  explanation: z.string(),
  isHighPopularity: z.boolean(),
  isHighMargin: z.boolean(),
});
export type MenuEngineeringFoodItem = z.infer<typeof MenuEngineeringFoodItem>;

export const MenuEngineeringIncompleteFoodItem = z.object({
  menuItemId: z.string(),
  foodName: z.string(),
  portions: z.number().int().nonnegative(),
  totalRevenueRial: z.number().int().nullable(),
  totalCostRial: z.number().int().nullable(),
  reason: MenuEngineeringIncompleteReason,
  explanation: z.string(),
});
export type MenuEngineeringIncompleteFoodItem = z.infer<typeof MenuEngineeringIncompleteFoodItem>;

export const MenuEngineeringAverages = z.object({
  totalPortions: z.number().int().nonnegative(),
  averagePortionsPerFood: z.number().int().nonnegative(),
  averageSharePerMille: z.number().int().nonnegative(),
  popularityThresholdPercent: z.number().int().positive(),
  popularityThresholdSharePerMille: z.number().int().nonnegative(),
  weightedAverageMarginRial: z.number().int(),
  marginThresholdPercent: z.number().int().positive(),
  marginThresholdRial: z.number().int(),
  totalFoodsCount: z.number().int().nonnegative(),
  validFoodsCount: z.number().int().nonnegative(),
  incompleteFoodsCount: z.number().int().nonnegative(),
});
export type MenuEngineeringAverages = z.infer<typeof MenuEngineeringAverages>;

export const MenuEngineeringGroups = z.object({
  star: z.array(MenuEngineeringFoodItem),
  plowhorse: z.array(MenuEngineeringFoodItem),
  puzzle: z.array(MenuEngineeringFoodItem),
  dog: z.array(MenuEngineeringFoodItem),
});
export type MenuEngineeringGroups = z.infer<typeof MenuEngineeringGroups>;

export const MenuEngineeringResponse = z.object({
  period: z.object({ from: BusinessDate, to: BusinessDate }),
  kitchenId: Id.nullable().optional(),
  averages: MenuEngineeringAverages,
  groups: MenuEngineeringGroups,
  items: z.array(MenuEngineeringFoodItem),
  incomplete: z.array(MenuEngineeringIncompleteFoodItem),
});
export type MenuEngineeringResponse = z.infer<typeof MenuEngineeringResponse>;

export const MenuEngineeringQuery = z.object({
  from: BusinessDate.optional(),
  to: BusinessDate.optional(),
  kitchenId: Id.optional(),
});
export type MenuEngineeringQuery = z.infer<typeof MenuEngineeringQuery>;

/** Format a number with Persian digits and standard comma separators. */
function faNumber(val: number): string {
  const formatted = Math.abs(val).toLocaleString("fa-IR");
  return formatted;
}

/**
 * Computes the 3-month Jalali range ending at the end of the current Jalali month.
 */
export function jalaliThreeMonthsRange(ref = new Date()): {
  startIso: BusinessDate;
  endIso: BusinessDate;
} {
  const currentMonth = jalaliMonthRange(ref);
  const endIso = currentMonth.endIso;

  // Start date of current Jalali month at noon UTC
  let d = new Date(currentMonth.startIso + "T12:00:00Z");

  // Step back 2 months to get the start of the 3-month period
  for (let step = 0; step < 2; step++) {
    // 1 day before the 1st of the month = last day of previous month
    d = new Date(d.getTime() - 86_400_000);
    const parts = new Intl.DateTimeFormat("en-US-u-ca-persian", {
      timeZone: "Asia/Tehran",
      day: "numeric",
    }).formatToParts(d);
    const day = Number(parts.find((p) => p.type === "day")?.value ?? "1");
    // Go to the 1st of that month
    d = new Date(d.getTime() - (day - 1) * 86_400_000);
  }

  const startIso = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tehran" }).format(d);
  return { startIso, endIso };
}

export interface MenuEngineeringOptions {
  popularityThresholdPercent?: number | undefined;
  marginThresholdPercent?: number | undefined;
}

/**
 * Aggregates ProfitFoodRow entries per food and categorizes into the 4 menu-engineering groups.
 */
export function computeMenuEngineering(
  rows: readonly ProfitFoodRow[],
  options: MenuEngineeringOptions = {},
): {
  averages: MenuEngineeringAverages;
  groups: MenuEngineeringGroups;
  items: MenuEngineeringFoodItem[];
  incomplete: MenuEngineeringIncompleteFoodItem[];
} {
  const popularityThresholdPercent = options.popularityThresholdPercent ?? 70;
  const marginThresholdPercent = options.marginThresholdPercent ?? 100;

  interface FoodAgg {
    menuItemId: string;
    foodName: string;
    portions: number;
    revenueRial: number;
    costRial: number;
    hasMissingPrice: boolean;
    hasMissingCost: boolean;
  }

  const aggs = new Map<string, FoodAgg>();

  for (const r of rows) {
    if (r.isSpecialPortion || r.menuItemId === "special-portion") {
      continue;
    }

    let item = aggs.get(r.menuItemId);
    if (!item) {
      item = {
        menuItemId: r.menuItemId,
        foodName: r.foodName,
        portions: 0,
        revenueRial: 0,
        costRial: 0,
        hasMissingPrice: false,
        hasMissingCost: false,
      };
      aggs.set(r.menuItemId, item);
    }

    item.portions += r.portions;

    if (r.status === "missing_price" || r.status === "missing_both" || r.revenueRial == null) {
      item.hasMissingPrice = true;
    } else {
      item.revenueRial += r.revenueRial;
    }

    if (r.status === "missing_cost" || r.status === "missing_both" || r.costRial == null) {
      item.hasMissingCost = true;
    } else {
      item.costRial += r.costRial;
    }
  }

  const validFoods: FoodAgg[] = [];
  const incomplete: MenuEngineeringIncompleteFoodItem[] = [];

  for (const item of aggs.values()) {
    if (item.hasMissingPrice && item.hasMissingCost) {
      incomplete.push({
        menuItemId: item.menuItemId,
        foodName: item.foodName,
        portions: item.portions,
        totalRevenueRial: null,
        totalCostRial: null,
        reason: "missing_both",
        explanation: "فاقد قیمت و بهای تمام‌شده",
      });
    } else if (item.hasMissingCost) {
      incomplete.push({
        menuItemId: item.menuItemId,
        foodName: item.foodName,
        portions: item.portions,
        totalRevenueRial: item.revenueRial,
        totalCostRial: null,
        reason: "missing_cost",
        explanation: "فاقد بهای تمام‌شده",
      });
    } else if (item.hasMissingPrice) {
      incomplete.push({
        menuItemId: item.menuItemId,
        foodName: item.foodName,
        portions: item.portions,
        totalRevenueRial: null,
        totalCostRial: item.costRial,
        reason: "missing_price",
        explanation: "فاقد قیمت فروش",
      });
    } else if (item.portions > 0) {
      validFoods.push(item);
    }
  }

  const totalFoodsCount = aggs.size;
  const validFoodsCount = validFoods.length;
  const incompleteFoodsCount = incomplete.length;

  if (validFoodsCount === 0) {
    const emptyAverages: MenuEngineeringAverages = {
      totalPortions: 0,
      averagePortionsPerFood: 0,
      averageSharePerMille: 0,
      popularityThresholdPercent,
      popularityThresholdSharePerMille: 0,
      weightedAverageMarginRial: 0,
      marginThresholdPercent,
      marginThresholdRial: 0,
      totalFoodsCount,
      validFoodsCount: 0,
      incompleteFoodsCount,
    };
    return {
      averages: emptyAverages,
      groups: { star: [], plowhorse: [], puzzle: [], dog: [] },
      items: [],
      incomplete,
    };
  }

  let totalPortions = 0;
  let totalMargin = 0;

  for (const f of validFoods) {
    totalPortions += f.portions;
    totalMargin += f.revenueRial - f.costRial;
  }

  const averagePortionsPerFood = Math.round(totalPortions / validFoodsCount);
  const averageSharePerMille = Math.round(1000 / validFoodsCount);
  const popularityThresholdSharePerMille = Math.round(
    (averageSharePerMille * popularityThresholdPercent) / 100,
  );

  const weightedAverageMarginRial = totalPortions > 0 ? Math.round(totalMargin / totalPortions) : 0;
  const marginThresholdRial = Math.round((weightedAverageMarginRial * marginThresholdPercent) / 100);

  const star: MenuEngineeringFoodItem[] = [];
  const plowhorse: MenuEngineeringFoodItem[] = [];
  const puzzle: MenuEngineeringFoodItem[] = [];
  const dog: MenuEngineeringFoodItem[] = [];
  const items: MenuEngineeringFoodItem[] = [];

  for (const f of validFoods) {
    const marginTotal = f.revenueRial - f.costRial;
    const marginPerPortion = f.portions > 0 ? Math.round(marginTotal / f.portions) : 0;
    const sharePerMille = totalPortions > 0 ? Math.round((f.portions * 1000) / totalPortions) : 0;

    // Popularity comparison: portions / totalPortions >= (popularityThresholdPercent / 100) * (1 / validFoodsCount)
    // Avoid floating point division by cross-multiplication:
    // f.portions * validFoodsCount * 100 >= totalPortions * popularityThresholdPercent
    const isHighPopularity = f.portions * validFoodsCount * 100 >= totalPortions * popularityThresholdPercent;
    const isHighMargin = marginPerPortion >= marginThresholdRial;

    let group: MenuEngineeringGroup;
    if (isHighPopularity && isHighMargin) {
      group = "star";
    } else if (isHighPopularity && !isHighMargin) {
      group = "plowhorse";
    } else if (!isHighPopularity && isHighMargin) {
      group = "puzzle";
    } else {
      group = "dog";
    }

    const sharePercent = Math.round(sharePerMille / 10);
    const marginDiff = marginPerPortion - weightedAverageMarginRial;
    const diffAbsFormatted = faNumber(marginDiff);
    const shareFormatted = Math.abs(sharePercent).toLocaleString("fa-IR");

    const explanation =
      marginDiff >= 0
        ? `${shareFormatted}٪ فروش، حاشیه ${diffAbsFormatted} ریال بالاتر از میانگین`
        : `${shareFormatted}٪ فروش، حاشیه ${diffAbsFormatted} ریال پایین‌تر از میانگین`;

    const foodItem: MenuEngineeringFoodItem = {
      menuItemId: f.menuItemId,
      foodName: f.foodName,
      portions: f.portions,
      popularitySharePerMille: sharePerMille,
      marginPerPortionRial: marginPerPortion,
      totalRevenueRial: f.revenueRial,
      totalCostRial: f.costRial,
      totalMarginRial: marginTotal,
      group,
      explanation,
      isHighPopularity,
      isHighMargin,
    };

    items.push(foodItem);

    if (group === "star") star.push(foodItem);
    else if (group === "plowhorse") plowhorse.push(foodItem);
    else if (group === "puzzle") puzzle.push(foodItem);
    else dog.push(foodItem);
  }

  // Sort within groups by portions descending
  const byPortionsDesc = (a: MenuEngineeringFoodItem, b: MenuEngineeringFoodItem) => b.portions - a.portions;
  star.sort(byPortionsDesc);
  plowhorse.sort(byPortionsDesc);
  puzzle.sort(byPortionsDesc);
  dog.sort(byPortionsDesc);
  items.sort(byPortionsDesc);

  const averages: MenuEngineeringAverages = {
    totalPortions,
    averagePortionsPerFood,
    averageSharePerMille,
    popularityThresholdPercent,
    popularityThresholdSharePerMille,
    weightedAverageMarginRial,
    marginThresholdPercent,
    marginThresholdRial,
    totalFoodsCount,
    validFoodsCount,
    incompleteFoodsCount,
  };

  return {
    averages,
    groups: { star, plowhorse, puzzle, dog },
    items,
    incomplete,
  };
}
